import cx from 'classnames';
import {map, take} from 'lodash';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Divider, InformationModal} from 'components';
import {UserContext} from 'context';
import {BillingPlatformInvoiceStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformInvoice} from 'types';
import {formatDate, transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.INVOICES;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    invoices: BillingPlatformInvoice[];
}

const INITIAL_ITEM_COUNT = 3;

export const InvoicesGrid: React.FC<Props> = ({invoices}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);

    const hasViewAllBtn = useMemo(() => invoices?.length > INITIAL_ITEM_COUNT, [invoices]);
    const initialInvoices = useMemo(() => take(invoices, INITIAL_ITEM_COUNT), [invoices]);
    const formatPaymentDate = useCallback((paymentDate: Date) => formatDate(paymentDate, timeZone, dateFormat), [dateFormat, timeZone]);
    const [modalOpen, setModalOpen] = useState(false);

    const renderInvoiceItem = useCallback((invoice: BillingPlatformInvoice, index: number, lastIndex: number) => (
        <Fragment key={index}>
            <div className='tw-flex tw-justify-between'>
                <div>{`${invoice.planName} x ${invoice.planQuantity}`}</div>
                <div className='tw-font-bold'>{transformUtils.formatPrice(invoice.total, invoice.currency)}</div>
            </div>
            <div className='tw-flex tw-justify-between'>
                <div className='tw-text-default-dimmed'>{formatPaymentDate(invoice.date)}</div>
                <div className={cx('tw-font-bold', {
                    'tw-text-success': invoice.status === BillingPlatformInvoiceStatus.Paid
                })}
                >
                    {invoice.status}
                </div>
            </div>
            {index !== lastIndex && <Divider className='tw-my-2' />}
        </Fragment>
    ), [formatPaymentDate]);

    const renderInvoices = useCallback((items: BillingPlatformInvoice[]) =>
        map(items, (payment, index) => renderInvoiceItem(payment, index, items.length - 1)), [renderInvoiceItem]);

    return (
        <div className='sm:tw-hidden'>
            {renderInvoices(initialInvoices)}
            {hasViewAllBtn &&
                <div className='tw-text-right'>
                    <Button className='tw-mt-4' onClick={() => setModalOpen(true)}>{t(T_KEYS_COMMON.BUTTONS.VIEW_ALL)}</Button>
                </div>}
            <InformationModal
                title={t(T_KEYS.VIEW_ALL_MODAL.TITLE)}
                open={modalOpen}
                maxWidth='xs'
                fullWidth
                onClose={() => setModalOpen(false)}
            >
                {renderInvoices(invoices)}
            </InformationModal>
        </div>
    );
};